import * as React from "react";
import { Redirect, Route } from "react-router-dom";

import { cs }             from "@Common/react/chain-services";
import { consumeContext } from "@Common/react/context";

export type RouteNode = {
    path: string;
    component?: string;
    userLevels?: Array<3 | 4 | 5>;
    redirect?: string;
    children?: Record<string, RouteNode>;
    render?: (params: any) => React.ReactNode;
};

const flattenRoutes = (routeTree: Record<string, RouteNode>, basePath = "") => {
    const flatRoutes: Array<RouteNode> = [];

    const traverse = (node: RouteNode, currentPath: string) => {
        const fullPath = `${currentPath}${node.path}`;
        const fullRedirect = `${currentPath}${node.path}${node.redirect}`;

        if (node) {
            flatRoutes.push({
                path: fullPath,
                ...(node.component ? {component: node.component} : {}),
                ...(node.userLevels ? {userLevels: node.userLevels} : {}),
                ...(node?.redirect ? {redirect: fullRedirect} : {}),
            });
        }

        if (node.children) {
            Object.values(node.children).forEach((childNode) => traverse(childNode, fullPath));
        }
    };

    Object.values(routeTree).forEach((rootNode) => traverse(rootNode, basePath));
    return flatRoutes;
};

const renderRoute = ({components, route, params, blockGuest, defaultRoute}
    : {components: React.ComponentType<any>[], route: RouteNode, params: any, blockGuest: boolean, defaultRoute: string}
) => (
    cs(
        consumeContext("userLevels"),
        ({ userLevels }) => {
            const { component, redirect, userLevels: routeLevels } = route;
            if (redirect) {
                return <Redirect to={redirect} />;
            }
            if (blockGuest && userLevels?.isGuest) {
                return <Redirect to="/" />;
            }
            const Component = components.find((c) => c.name === component);
            if (!routeLevels || routeLevels.includes(userLevels.userLevel)) {
                return Component ? <Component {...params} /> : route?.render(params);
            }
            return <Redirect {...{to: defaultRoute}} />;
        }
    )
);

export const renderRoutes = ({components, contextComponents, routes, defaultRoute, blockGuest, next} :
    {components: React.ComponentType<any>[], contextComponents: React.FunctionComponent<any>[], routes: Record<string, RouteNode>, defaultRoute: string, blockGuest: boolean, next: (children: React.ReactNode) => React.ReactNode}
) => (
    cs(
        ...contextComponents.map(
            (Component) => ({}, next) => Component({ next })
        ),
        ({}) => next(flattenRoutes(routes).map((route, i) => (
            <Route
                key={i}
                exact
                path={route.path}
                render={(params) => renderRoute({components, route, params, blockGuest, defaultRoute})}
            />
        )))
    )
);
